/**
 * The hand: drag, click and keyboard over one Carry machine (`Gestures`).
 *
 * Screens register what can be picked up (`source`) and where it can land
 * (`target`); the field adds camera maths for its WebGL bits (`resolver`) and
 * keyboard stops that have no element (`virtual`). Every input goes through
 * the machine in `carry.ts`, and every effect it makes goes to the page's
 * `effect` (Actions): fire, confirm, need, announce, presence, ghost, ring.
 *
 * While something is carried, targets that take it glow and the rest dim
 * with the reason in their tooltip (`data-carry`), decided during the carry,
 * not at the drop. A write dropped on a World scrubbed back in time returns
 * it to Live first.
 */

import type {
  CarryEffect, CarryState, CreateGestures, Gestures, Held, Modifier, Region, Target, Unsub, Verb, VerbContext,
} from '../contracts';
import { idle, inner, step, type CarryCtx, type HandInput } from './carry';
import { bindPointer, createPointerCore } from './dnd';
import { bindKeyboard } from './keyboard';
import { createRegistry, scrubs } from './targets';
import { createVerbs, launchVerbs, pick, ringOf, targetLabel } from './verbs';

export { CARRY_CONTROL } from './dnd';
export { scrubs } from './targets';
export { nouns, worldClock } from './verbs';


const argsWorld = (e: CarryEffect): string | null => {
  const args = e.type === 'fire' || e.type === 'confirm' ? e.args : e.type === 'need' ? e.partial : null;
  return args && typeof args.w === 'string' ? args.w : null;
};

export const createGestures: CreateGestures = (deps) => {
  const registry = createRegistry();
  const curated = createVerbs(deps.clock ? { clock: deps.clock } : {});
  let state: CarryState = idle();
  const watchers = new Set<(s: CarryState) => void>();
  let cache: { catalogue: VerbContext['catalogue'] | null; verbs: readonly Verb[] } = { catalogue: null, verbs: [] };

  /** The curated verbs whose operation the spec still has, and a launch verb for every operation. */
  const verbs = (): readonly Verb[] => {
    const catalogue = deps.verbContext().catalogue;
    if (cache.catalogue !== catalogue) {
      const known = catalogue.ops.length > 0;
      const live = known ? curated.filter((v) => catalogue.byId(v.op) !== undefined) : curated;
      cache = { catalogue, verbs: [...live, ...launchVerbs(catalogue)] };
    }
    return cache.verbs;
  };

  /** An operation the spec does not name counts as a write: a drop on a scrubbed World returns it to Live first. */
  const writes = (op: string): boolean => deps.verbContext().catalogue.byId(op)?.method !== 'GET';

  /** Screens' own drops (Gestures.local), newest first. */
  const locals: ((held: Held, over: Target) => string | null)[] = [];
  const localWords = (held: Held, over: Target): string | null => {
    for (let i = locals.length - 1; i >= 0; i -= 1) {
      try {
        const w = locals[i]!(held, over);
        if (w) return w;
      } catch { /* a screen that failed to judge takes nothing */ }
    }
    return null;
  };

  const ctx = (): CarryCtx => ({
    ...deps.verbContext(),
    verbs: verbs(),
    targets: () => registry.all(),
    scrubbed: (world) => scrubs.seq(world),
    writes,
    groupOf: (t) => registry.groupOf(t.id),
    leadOf: (g) => registry.leadOf(g),
    ...(locals.length ? { local: localWords } : {}),
  });

  // ---------- painting what takes the carried thing ----------

  const titles = new WeakMap<HTMLElement, string | null>();
  let painted = '';
  let lastOver: HTMLElement | null = null;
  const clearPaint = (): void => {
    for (const e of [...registry.targets(), ...registry.sources()]) {
      if (!e.el.hasAttribute('data-carry')) continue;
      e.el.removeAttribute('data-carry');
      e.el.removeAttribute('data-carry-over');
      if (titles.has(e.el)) {
        const was = titles.get(e.el) ?? null;
        if (was === null) e.el.removeAttribute('title');
        else e.el.setAttribute('title', was);
        titles.delete(e.el);
      }
    }
  };
  const paint = (): void => {
    const held = state.held;
    const key = held && state.phase !== 'pressed' && state.phase !== 'ring' ? `${state.phase}|${held.id}|${[...state.mods].sort().join(',')}|${inner(state).only ?? ''}` : '';
    if (key !== painted) {
      painted = key;
      clearPaint();
      if (key && held) {
        const c = ctx();
        const only = inner(state).only;
        const list = only ? c.verbs.filter((v) => v.id === only) : c.verbs;
        for (const e of registry.targets()) {
          const t = e.target();
          if (!t) continue;
          const choice = pick(list, held, t, state.mods, c);
          // A screen's own drop (Gestures.local) glows like a verb's.
          const own = choice.verb || only ? null : c.local?.(held, t) ?? null;
          e.el.setAttribute('data-carry', choice.verb || own ? 'accept' : 'reject');
          if (!titles.has(e.el)) titles.set(e.el, e.el.getAttribute('title'));
          e.el.setAttribute('title', choice.verb ? `${choice.verb.label(held, t)}` : own ?? `${targetLabel(t)}: ${choice.why}`);
        }
        for (const s of registry.sources()) if (s.held()?.id === held.id) s.el.setAttribute('data-carry', 'held');
      }
    }
    const overEl = state.held && state.over ? registry.elementFor(state.over.id) : null;
    if (lastOver && lastOver !== overEl) lastOver.removeAttribute('data-carry-over');
    if (overEl) overEl.setAttribute('data-carry-over', state.verdict ?? 'ok');
    lastOver = overEl;
    ring(state);
  };

  // A focus ring for keyboard stops that have no element (bits drawn only in WebGL).
  const focusRing = typeof document === 'undefined' ? null : document.createElement('div');
  if (focusRing) {
    focusRing.className = 'pg-carry-ring';
    focusRing.setAttribute('aria-hidden', 'true');
    focusRing.hidden = true;
    deps.root.append(focusRing);
  }
  const ring = (s: CarryState): void => {
    if (!focusRing) return;
    const id = s.phase === 'keyboard' ? s.over?.id : undefined;
    const rect = id && !registry.elementFor(id) ? registry.rectFor(id) : null;
    focusRing.hidden = !rect;
    if (rect) {
      focusRing.style.setProperty('left', `${rect.x}px`);
      focusRing.style.setProperty('top', `${rect.y}px`);
      focusRing.style.setProperty('width', `${rect.width}px`);
      focusRing.style.setProperty('height', `${rect.height}px`);
    }
  };

  // ---------- running the machine ----------

  const center = (id: string | undefined): { x: number; y: number } | null => {
    if (!id) return null;
    const el = registry.elementFor(id);
    const r = el?.getBoundingClientRect?.() ?? registry.rectFor(id);
    return r ? { x: r.x + r.width / 2, y: r.y + r.height / 2 } : null;
  };

  const route = (e: CarryEffect, overId: string | undefined): void => {
    if (e.type === 'fire' || e.type === 'confirm' || e.type === 'need') {
      const world = argsWorld(e);
      if (world && scrubs.seq(world) !== null && writes(e.verb.op)) scrubs.live(world);
    }
    if ((e.type === 'fire' || e.type === 'confirm') && e.origin === null) {
      const at = center(overId);
      if (at) {
        deps.effect({ ...e, origin: at });
        return;
      }
    }
    deps.effect(e);
  };

  const dispatch = (i: HandInput): void => {
    const before = state;
    const out = step(state, i, ctx());
    state = out.state;
    const overId = before.over?.id ?? (i.type === 'click' && 'target' in i.thing ? i.thing.target.id : undefined);
    if (out.live) scrubs.live(out.live);
    for (const e of out.effects) route(e, overId);
    paint();
    const byKey = i.type === 'key-lift' || i.type === 'key-cycle';
    if ((state.phase === 'keyboard' || (state.phase === 'carrying' && byKey)) && state.over && state.over.id !== before.over?.id) {
      const el = registry.elementFor(state.over.id);
      if (el && typeof document !== 'undefined' && document.activeElement !== el) el.focus();
    } else if (i.type === 'refresh' && state.phase === 'keyboard' && state.over && typeof document !== 'undefined') {
      // The aimed element was drawn again (the Loom's ticks after a flip): focus follows it rather than staying on the old one.
      const active = document.activeElement;
      const lost = !active || active === document.body || !(active as HTMLElement).isConnected;
      const el = registry.elementFor(state.over.id);
      if (lost && el) el.focus();
    }
    if (state !== before) for (const fn of [...watchers]) fn(state);
  };

  // ---------- the roads in ----------

  const carried = (): boolean => !!state.held && state.phase !== 'idle' && state.phase !== 'ring';

  /** Whether the thing carried now would drop on `t` (for hit-testing: the first target out from the pointer that takes it). */
  const accepts = (t: Target): boolean => {
    const held = state.held;
    if (!held) return true;
    const c = ctx();
    const only = inner(state).only;
    return pick(only ? c.verbs.filter((v) => v.id === only) : c.verbs, held, t, state.mods, c).verb !== null;
  };
  const resolveHere = (x: number, y: number): Target | null => registry.resolve(x, y, carried() ? accepts : undefined);

  /**
   * Targets that come or go while something is carried (the Loom's ticks after
   * a windows read) change what the keyboard can reach: judge again, once per
   * turn of the event loop however many registered.
   */
  let rejudge = false;
  const targetsChanged = (): void => {
    painted = '';
    if (rejudge || !carried()) return;
    rejudge = true;
    const later = typeof queueMicrotask === 'function' ? queueMicrotask : (fn: () => void) => void Promise.resolve().then(fn);
    later(() => {
      rejudge = false;
      if (carried()) dispatch({ type: 'refresh' });
    });
  };

  const voidFor = (held: Held): Target | null => {
    for (const e of registry.targets()) {
      const t = e.target();
      if (t && t.kind === 'gate:void' && (!held.world || !t.world || t.world === held.world)) return t;
    }
    return null;
  };
  const core = createPointerCore({
    dispatch,
    state: () => state,
    resolve: resolveHere,
    voidFor,
    timers: { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>) },
    now: () => (typeof performance === 'undefined' ? Date.now() : performance.now()),
  });
  const pointer = bindPointer({ root: deps.root, core, registry, state: () => state, dispatch, resolve: resolveHere });
  const keyboard = bindKeyboard({ root: deps.root, registry, state: () => state, dispatch, announce: (t) => deps.announce.polite(t) });

  /** Hover teaches the operation: a target's title names the operations it answers. */
  const teach = (el: HTMLElement, subject: () => Held | Target | null): Unsub => {
    const enter = (): void => {
      // Only titles this hand wrote are rewritten; a screen's own title stays.
      if (state.phase !== 'idle' || (el.getAttribute('title') && !el.hasAttribute('data-taught'))) return;
      const s = subject();
      if (!s) return;
      const ops = [...new Set(ringOf(verbs(), s).map((v) => v.op))];
      if (ops.length === 0) return;
      const name = 'label' in s && typeof s.label === 'string' ? s.label : targetLabel(s as Target);
      el.setAttribute('title', `${name} → ${ops.join(', ')}`);
      el.setAttribute('data-taught', '');
    };
    el.addEventListener('pointerenter', enter);
    el.addEventListener('focus', enter);
    return () => {
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('focus', enter);
    };
  };

  const api: Gestures = {
    source(el: HTMLElement, held: () => Held | null, region: Region): Unsub {
      const offs = [registry.addSource(el, held, region), pointer.bindSource(el, held), teach(el, held)];
      if (!el.hasAttribute('role') && el.tagName !== 'BUTTON') el.setAttribute('role', 'button');
      keyboard.adopt(el, region);
      return () => { for (const off of offs) off(); };
    },
    target(el: HTMLElement, target: () => Target | null, region: Region): Unsub {
      const offs = [registry.addTarget(el, target, region)];
      if (!registry.sourceOf(el)) offs.push(teach(el, target));
      keyboard.adopt(el, region);
      targetsChanged(); // a target that appears mid-carry is judged, and reachable, too
      return () => {
        for (const off of offs) off();
        targetsChanged();
      };
    },
    resolver: (fn) => registry.addResolver(fn),
    virtual: (fn) => {
      const off = registry.addVirtual(fn);
      targetsChanged();
      return () => {
        off();
        targetsChanged();
      };
    },
    lift(held: Held, via: 'click' | 'keyboard'): void {
      if (state.phase !== 'idle') dispatch({ type: 'cancel' });
      dispatch(via === 'keyboard' ? { type: 'key-lift', held } : { type: 'click', thing: { held } });
    },
    cancel: () => dispatch({ type: 'cancel' }),
    ring: (subject) => ringOf(verbs(), subject),
    verbs,
    state: () => state,
    onState(fn) {
      watchers.add(fn);
      return () => watchers.delete(fn);
    },
    setModifier: (mod: Modifier, on: boolean) => dispatch({ type: 'mod', mod, on }),
    perform(verbId, held, target) {
      if (state.phase !== 'idle') dispatch({ type: 'cancel' });
      dispatch({ type: 'key-lift', held, only: verbId });
      if (target) {
        dispatch({ type: 'move', at: center(target.id) ?? { x: 0, y: 0 }, over: target });
        dispatch({ type: 'key-drop' });
      }
    },
    choose: (index) => dispatch({ type: 'choose', index }),
    refresh: () => dispatch({ type: 'refresh' }),
    local(fn) {
      locals.push(fn);
      targetsChanged();
      return () => {
        const i = locals.lastIndexOf(fn);
        if (i >= 0) locals.splice(i, 1);
        targetsChanged();
      };
    },
  };
  return api;
};

